import {
  Descriptions,
  Table,
  Button,
  Badge,
  Space,
  Typography,
  Progress,
  Spin,
  Modal,
  Input,
} from 'antd';
import { useState } from 'react';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useSalesOrderById, useCancelSO } from '@hooks/useSales';
import type {
  SalesOrderLineDto,
  SalesOrderStatus,
} from '@/types/sales';
import { downloadSalesOrderPdf } from '@/api/exportApi';
import { useAuth } from '@stores/AuthContext';
import { mdCanWriteModule } from '@stores/modulePermissions';
import CreateInvoiceModal from '@modules/finance/CreateInvoiceModal';

const MD_STATUS_COLOR_MAP: Record<SalesOrderStatus, string> = {
  Draft: 'default',
  Confirmed: 'processing',
  Processing: 'warning',
  PartiallyShipped: 'orange',
  Shipped: 'cyan',
  Delivered: 'success',
  Cancelled: 'error',
};

const MD_STATUS_LABEL_MAP: Record<SalesOrderStatus, string> = {
  Draft: 'Чернетка',
  Confirmed: 'Підтверджено',
  Processing: 'В обробці',
  PartiallyShipped: 'Частково відвантажено',
  Shipped: 'Відвантажено',
  Delivered: 'Доставлено',
  Cancelled: 'Скасовано',
};

interface SalesOrderDetailPageProps {
  orderId: string;
  onBack: () => void;
  /** Рахунок уже створено — відкрити його картку */
  onInvoiceCreated: (mdInvoiceId: string) => void;
}

export default function SalesOrderDetailPage({
  orderId,
  onBack,
  onInvoiceCreated,
}: SalesOrderDetailPageProps) {
  const [mdIsCancelModalOpen, setMdIsCancelModalOpen] = useState(false);
  const [mdIsInvoiceModalOpen, setMdIsInvoiceModalOpen] = useState(false);
  const { user } = useAuth();
  const [mdCancelReason, setMdCancelReason] = useState('');

  const { data: mdOrder, isLoading: mdIsLoading } = useSalesOrderById(orderId);
  const mdCancelSO = useCancelSO();

  const mdHandleCancel = () => {
    mdCancelSO.mutate(
      { id: orderId, reason: mdCancelReason },
      {
        onSuccess: () => {
          setMdIsCancelModalOpen(false);
          setMdCancelReason('');
        },
      }
    );
  };

  const mdCanCancel =
    mdOrder?.status === 'Draft' ||
    mdOrder?.status === 'Confirmed' ||
    mdOrder?.status === 'Processing';

  // POST /api/invoices — модуль Finance, потрібен повний доступ (менеджер продажів отримав би 403)
  const mdCanCreateInvoice =
    mdCanWriteModule(user?.roles ?? [], 'Finance') &&
    (mdOrder?.status === 'Confirmed' ||
      mdOrder?.status === 'Processing' ||
      mdOrder?.status === 'PartiallyShipped' ||
      mdOrder?.status === 'Shipped' ||
      mdOrder?.status === 'Delivered');

  const mdLineColumns: ColumnsType<SalesOrderLineDto> = [
    {
      title: 'SKU',
      dataIndex: 'productSku',
      key: 'productSku',
      width: 120,
    },
    {
      title: 'Назва',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Замовлено',
      dataIndex: 'quantityOrdered',
      key: 'quantityOrdered',
      width: 110,
      align: 'right',
    },
    {
      title: 'Відвантажено',
      dataIndex: 'quantityShipped',
      key: 'quantityShipped',
      width: 130,
      align: 'right',
    },
    {
      title: 'Залишилось',
      dataIndex: 'quantityPending',
      key: 'quantityPending',
      width: 110,
      align: 'right',
    },
    {
      title: 'Прогрес',
      key: 'progress',
      width: 160,
      render: (_: unknown, mdRecord: SalesOrderLineDto) => {
        const mdPercent =
          mdRecord.quantityOrdered > 0
            ? Math.round((mdRecord.quantityShipped / mdRecord.quantityOrdered) * 100)
            : 0;
        return <Progress percent={mdPercent} size="small" />;
      },
    },
    {
      title: 'Ціна',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Знижка %',
      dataIndex: 'discountPercent',
      key: 'discountPercent',
      width: 100,
      align: 'right',
      render: (mdValue: number) => `${mdValue}%`,
    },
    {
      title: 'Сума',
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 130,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
  ];

  if (mdIsLoading || !mdOrder) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Назад
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Замовлення {mdOrder.number}
        </Typography.Title>
        <Badge
          status={
            MD_STATUS_COLOR_MAP[mdOrder.status] as
              | 'default'
              | 'processing'
              | 'success'
              | 'error'
              | 'warning'
          }
          text={MD_STATUS_LABEL_MAP[mdOrder.status]}
        />
      </Space>

      <Descriptions bordered column={3} size="small" style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Клієнт">{mdOrder.customerName}</Descriptions.Item>
        <Descriptions.Item label="Дата замовлення">
          {dayjs(mdOrder.orderDate).format('DD.MM.YYYY')}
        </Descriptions.Item>
        <Descriptions.Item label="Очікуване відвантаження">
          {mdOrder.expectedShipmentDate
            ? dayjs(mdOrder.expectedShipmentDate).format('DD.MM.YYYY')
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Валюта">{mdOrder.currency}</Descriptions.Item>
        <Descriptions.Item label="Курс">{mdOrder.exchangeRate}</Descriptions.Item>
        <Descriptions.Item label="Сума">
          {mdOrder.totalAmount.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}{' '}
          {mdOrder.currency}
        </Descriptions.Item>
        {mdOrder.shippingAddress && (
          <Descriptions.Item label="Адреса доставки" span={3}>
            {mdOrder.shippingAddress}
          </Descriptions.Item>
        )}
        {mdOrder.notes && (
          <Descriptions.Item label="Примітки" span={3}>
            {mdOrder.notes}
          </Descriptions.Item>
        )}
        {mdOrder.cancelReason && (
          <Descriptions.Item label="Причина скасування" span={3}>
            <Typography.Text type="danger">{mdOrder.cancelReason}</Typography.Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      <Typography.Title level={5}>Рядки замовлення</Typography.Title>

      <Table<SalesOrderLineDto>
        columns={mdLineColumns}
        dataSource={mdOrder.lines}
        rowKey="id"
        pagination={false}
        size="small"
      />

      <div
        style={{
          padding: '16px 0',
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          borderTop: '1px solid #f0f0f0',
          marginTop: 16,
        }}
      >
        <Button
          icon={<PrinterOutlined />}
          onClick={() => downloadSalesOrderPdf(orderId)}
        >
          Друк накладної
        </Button>
        {mdCanCreateInvoice && (
          <Button type="primary" onClick={() => setMdIsInvoiceModalOpen(true)}>
            Створити рахунок
          </Button>
        )}
        {mdCanCancel && (
          <Button danger onClick={() => setMdIsCancelModalOpen(true)}>
            Відмінити
          </Button>
        )}
      </div>

      <CreateInvoiceModal
        open={mdIsInvoiceModalOpen}
        salesOrder={mdOrder}
        onClose={() => setMdIsInvoiceModalOpen(false)}
        onCreated={(mdInvoiceId) => {
          setMdIsInvoiceModalOpen(false);
          onInvoiceCreated(mdInvoiceId);
        }}
      />

      <Modal
        title="Скасування замовлення"
        open={mdIsCancelModalOpen}
        onOk={mdHandleCancel}
        onCancel={() => setMdIsCancelModalOpen(false)}
        confirmLoading={mdCancelSO.isPending}
        okText="Скасувати замовлення"
        okButtonProps={{ danger: true }}
        cancelText="Назад"
      >
        <Typography.Paragraph>
          Вкажіть причину скасування замовлення:
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          value={mdCancelReason}
          onChange={(mdEvent) => setMdCancelReason(mdEvent.target.value)}
          placeholder="Причина скасування..."
        />
      </Modal>
    </>
  );
}
